/**
 * Parking Data Service - Centralized Data Management
 * 
 * This service manages parking-related data across multiple components in the
 * parking management system. It provides centralized data storage, state management,
 * and data sharing between different parts of the application.
 * 
 * Key Features:
 * - Centralized parking data storage
 * - Multi-step form data management
 * - Component state synchronization
 * - Data persistence across navigation
 * - Form validation state tracking
 * - Progress tracking for multi-step processes
 * 
 * Data Types Managed:
 * - Parking facility information
 * - Team member data
 * - Vehicle capacity configurations
 * - Facility settings
 * - Operating hours and timezone data
 * 
 * @author Parking System Team
 * @version 1.0.0
 */

import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

/**
 * Parking Data Interface
 * Defines the structure for parking-related data
 */
export interface ParkingData {
  // From parkings component
  parkingName?: string;
  parkingAddress?: string;
  parkingDescription?: string;
  parkingImages?: string[];
  
  // From parking-add-people component
  teamMembers?: any[];
  vendors?: any[];
  
  // From parking-add-capacity component
  parkingType?: string;
  vehicleCapacity?: any[];
  facilities?: any[];
  operatingHours?: any;
  timezone?: string;
  manager?: string;
}

/**
 * Component Step Data Interface
 * Tracks the current step for multi-step components
 */
export interface ComponentStepData {
  parkings: number;
  parkingAddPeople: number;
  parkingAddCapacity: number;
}

/**
 * Form Data Interface
 * Generic interface for form data storage
 */
export interface FormData {
  // Parkings component form data
  parkingsForm?: any;
  
  // Parking-add-people component form data
  parkingAddPeopleForm?: any;
  
  // Parking-add-capacity component form data
  parkingAddCapacityForm?: any;
}

@Injectable({
  providedIn: 'root'
})
export class ParkingDataService {
  private parkingDataSubject = new BehaviorSubject<ParkingData>({});
  public parkingData$ = this.parkingDataSubject.asObservable();
  
  private componentStepsSubject = new BehaviorSubject<ComponentStepData>({
    parkings: 1,
    parkingAddPeople: 1,
    parkingAddCapacity: 1
  });
  public componentSteps$ = this.componentStepsSubject.asObservable();
  
  private formDataSubject = new BehaviorSubject<FormData>({});
  public formData$ = this.formDataSubject.asObservable();

  constructor() {}

  // Update specific section of parking data
  updateParkingData(data: Partial<ParkingData>): void {
    const currentData = this.parkingDataSubject.value;
    const updatedData = { ...currentData, ...data };
    this.parkingDataSubject.next(updatedData);
  }

  // Get current parking data
  getParkingData(): ParkingData {
    return this.parkingDataSubject.value;
  }

  // Clear all parking data
  clearParkingData(): void {
    this.parkingDataSubject.next({});
  }

  // Set complete parking data
  setParkingData(data: ParkingData): void {
    this.parkingDataSubject.next(data);
  }

  // Preserve data during backward navigation
  preserveData(): void {
    // This method ensures data is not lost during backward navigation
    // The data is already stored in the BehaviorSubject, so it persists
    console.log('Data preserved for backward navigation:', this.parkingDataSubject.value);
  }

  // Save current step for a component
  saveComponentStep(componentName: keyof ComponentStepData, step: number): void {
    const currentSteps = this.componentStepsSubject.value;
    const updatedSteps = { ...currentSteps, [componentName]: step };
    this.componentStepsSubject.next(updatedSteps);
    
    // Save to localStorage
    localStorage.setItem(`parkingStep_${componentName}`, step.toString());
    console.log(`Saved step ${step} for ${componentName} to localStorage:`, updatedSteps);
  }

  // Get current step for a component
  getComponentStep(componentName: keyof ComponentStepData): number {
    // First try to get from localStorage
    const localStorageStep = localStorage.getItem(`parkingStep_${componentName}`);
    if (localStorageStep) {
      const step = parseInt(localStorageStep, 10);
      console.log(`Retrieved step ${step} for ${componentName} from localStorage`);
      return step;
    }
    
    // Fallback to BehaviorSubject
    return this.componentStepsSubject.value[componentName];
  }

  // Get all component steps
  getComponentSteps(): ComponentStepData {
    return this.componentStepsSubject.value;
  }

  // Save form data for a component
  saveFormData(componentName: keyof FormData, formData: any): void {
    const currentFormData = this.formDataSubject.value;
    const updatedFormData = { ...currentFormData, [componentName]: formData };
    this.formDataSubject.next(updatedFormData);
    
    // Save to localStorage
    localStorage.setItem(`parkingFormData_${componentName}`, JSON.stringify(formData));
    console.log(`Saved form data for ${componentName} to localStorage:`, formData);
  }

  // Get form data for a component
  getFormData(componentName: keyof FormData): any {
    // First try to get from localStorage
    const localStorageData = localStorage.getItem(`parkingFormData_${componentName}`);
    if (localStorageData) {
      try {
        const parsedData = JSON.parse(localStorageData);
        console.log(`Retrieved form data for ${componentName} from localStorage:`, parsedData);
        return parsedData;
      } catch (error) {
        console.error(`Error parsing localStorage data for ${componentName}:`, error);
      }
    }
    
    // Fallback to BehaviorSubject
    return this.formDataSubject.value[componentName];
  }

  // Get all form data
  getAllFormData(): FormData {
    return this.formDataSubject.value;
  }

  // Clear form data for a component
  clearFormData(componentName: keyof FormData): void {
    const currentFormData = this.formDataSubject.value;
    const updatedFormData = { ...currentFormData };
    delete updatedFormData[componentName];
    this.formDataSubject.next(updatedFormData);
    console.log(`Cleared form data for ${componentName}`);
  }

  // Clear all form data
  clearAllFormData(): void {
    this.parkingDataSubject.next({}); // Clear overall parking data
    this.componentStepsSubject.next({ // Reset steps
      parkings: 1,
      parkingAddPeople: 1,
      parkingAddCapacity: 1
    });
    this.formDataSubject.next({}); // Clear all form data
    
    // Clear all parking-related localStorage items
    const keysToRemove = [
      // Parkings component keys
      'parkingsStep2Data',
      'parkingsStep3Data', 
      'parkingsStep4Data',
      'combinedParkingData',
      'parking_id',
      'parkingFormData',
      'lastActiveComponent',
      'lastActiveStep',
      
      // Parking-add-people component keys
      'parkingAddPeopleStep2Data',
      'parkingAddPeopleStep3Data',
      'parkingAddPeopleStep4Data',
      
      // Parking data service keys
      'parkingFormData_parkings',
      'parkingFormData_parkingAddPeople', 
      'parkingFormData_parkingAddCapacity',
      'parkingStep_parkings',
      'parkingStep_parkingAddPeople',
      'parkingStep_parkingAddCapacity',

      // Manager assignment key (set after invite in wizard)
      'parking_invited_manager_id',
      'parking_team_member_data'
    ];
    
    // Remove specific keys
    keysToRemove.forEach(key => {
      localStorage.removeItem(key);
    });
    
    // Also clear any keys that start with these prefixes
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith('parkingFormData_') || 
          key.startsWith('parkingStep_') ||
          key.startsWith('parkingsStep') ||
          key.startsWith('parkingAddPeopleStep') ||
          key.startsWith('parkingAddCapacityStep')) {
        localStorage.removeItem(key);
      }
    });
    
    console.log('Cleared all parking setup data from localStorage and service');
    console.log('Parking setup process completed successfully!');
  }

  // Save parking_id to both sessionStorage and localStorage
  saveParkingId(parkingId: string): void {
    sessionStorage.setItem('parking_id', parkingId);
    localStorage.setItem('parking_id', parkingId);
    console.log('Saved parking_id to storage:', parkingId);
  }

  // Ensure parking ID is available for navigation
  ensureParkingIdAvailable(): string | null {
    // Try multiple sources to get parking ID
    let parkingId = this.getParkingId();
    
    if (!parkingId) {
      parkingId = sessionStorage.getItem('parking_id') || localStorage.getItem('parking_id');
    }
    
    if (parkingId) {
      this.saveParkingId(parkingId);
    }
    
    return parkingId;
  }

  // Get parking_id from sessionStorage or localStorage
  getParkingId(): string | null {
    return sessionStorage.getItem('parking_id') || localStorage.getItem('parking_id');
  }

  // Clear parking_id from both storages
  clearParkingId(): void {
    sessionStorage.removeItem('parking_id');
    localStorage.removeItem('parking_id');
    console.log('Cleared parking_id from storage');
  }
} 